import { useRef, useEffect, useState, useMemo } from "react";
import { StriimInputField, StriimForm, StriimButton } from "@striim/striim-ui";
import * as Yup from "yup";
import { Grid, Box } from "@mui/material";
import VaultsService from "./vaults-service";
import { mapOptions } from "../utils";
import { VaultTypes, httpMatch } from "./constants";
import FileSelectionModal from "./file-selection-modal";
import growl from "../../../../../../app/components/common/growl";
import { StriimTypography, StriimMessageBox } from "@striim/striim-ui-v2";

const sxStyles = {
    container: {
        marginBottom: 2
    },
    grid: {
        columnGap: 3,
        justifyContent: "space-between"
    },
    infoBoxContainer: {
        "& .MuiCard-root": {
            backgroundColor: ({ palette }) => palette.greyscale[100],
            borderColor: ({ palette }) => palette.greyscale[200],
            "& .MuiCardHeader-avatar": {
                "& svg": {
                    fill: ({ palette }) => palette.greyscale[600]
                }
            }
        }
    }
};

const CyberArkVault = ({ setFormValues, setIsValid, dataForEdit }) => {
    const [namespaceOptions, setNamespaceOptions] = useState([]);
    const [isCertificatePasswordEditable, setIsCertificatePasswordEditable] = useState(false);
    const [fileUploadVisibility, setFileUploadVisibility] = useState(false);
    const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

    const values = dataForEdit || {
        name: "",
        nsName: "",
        properties: {
            VaultType: VaultTypes.CYBERARK_VAULT,
            ConnectionURL: "",
            AppID: "",
            SafeName: "",
            CertificateName: "",
            CertificatePassword: ""
        }
    };

    useEffect(() => {
        async function fetchData() {
            try {
                const theData = await VaultsService.getNamespaces();
                setNamespaceOptions(theData?.models || []);
            } catch (error) {
                growl.error(error, "Failed to fetch namespaces:");
                setNamespaceOptions([]);
            } finally {
                setHasAttemptedFetch(true);
            }
        }

        if (!hasAttemptedFetch) {
            fetchData();
        }
    }, [hasAttemptedFetch]);

    const mappedNamespaceOptions = useMemo(() => mapOptions(namespaceOptions), [namespaceOptions]);
    const formRef = useRef();
    const certificatePasswordRef = useRef(null);

    useEffect(() => {
        if (isCertificatePasswordEditable) {
            certificatePasswordRef.current.focus();
        }
    }, [isCertificatePasswordEditable]);

    const handleCertificatePasswordField = event => {
        const value = event.target.value;
        if (dataForEdit && !value) {
            certificatePasswordRef.current.blur();
            setIsCertificatePasswordEditable(false);
        }
    };

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                name: Yup.string().required("Required"),
                nsName: Yup.string().required("Required"),
                properties: Yup.object({
                    ConnectionURL: Yup.string()
                        .required("Required")
                        .matches(httpMatch, "Please enter a valid URL"),
                    AppID: Yup.string().required("Required"),
                    SafeName: Yup.string().required("Required"),
                    CertificateName: Yup.string().nullable(),
                    CertificatePassword: dataForEdit ? null : Yup.string()
                })
            }),
        [dataForEdit]
    );

    return (
        <StriimForm values={values} validationSchema={validationSchema} formRef={formRef} validateOnMount={true}>
            {({ values, setFieldValue, isValid }) => {
                setFormValues(values);
                setIsValid(isValid);
                return (
                    <>
                        <Box sx={[sxStyles.infoBoxContainer, sxStyles.container]}>
                            <StriimMessageBox
                                type="INFO"
                                text={
                                    <StriimTypography variant="body4" color="greyscale.900">
                                        Vault values created with this type cannot be viewed after creation.
                                    </StriimTypography>
                                }
                            />
                        </Box>
                        <StriimInputField
                            required
                            name="name"
                            label="Name"
                            placeholder="Enter Vault name"
                            isFormElement
                            sx={sxStyles.container}
                            disabled={dataForEdit ? true : false}
                        />
                        <StriimInputField
                            required
                            name="nsName"
                            label="Namespace"
                            select
                            id="data-test-id-namespace-select"
                            placeholder="Namespace"
                            SelectProps={{
                                options: mappedNamespaceOptions,
                                menuPosition: "fixed"
                            }}
                            isFormElement
                            sx={sxStyles.container}
                            disabled={dataForEdit ? true : false}
                        />
                        <Grid container sx={{ ...sxStyles.container, ...sxStyles.grid }}>
                            <Grid item xs={7}>
                                <StriimInputField
                                    required
                                    name="properties.ConnectionURL"
                                    label="Connection URL"
                                    helperText='For example: "https://cyberark.example.com"'
                                    isFormElement
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <StriimInputField
                                    required
                                    name="properties.AppID"
                                    label="App ID"
                                    isFormElement
                                    helperText='For example: "myapp"'
                                />
                            </Grid>
                        </Grid>
                        <Grid container sx={{ ...sxStyles.container, ...sxStyles.grid, alignItems: "center" }}>
                            <Grid item xs={4}>
                                <StriimInputField
                                    required
                                    name="properties.SafeName"
                                    label="Safe Name"
                                    isFormElement
                                    helperText='For example: "MySafe"'
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <StriimInputField
                                    name="properties.CertificateName"
                                    label="Certificate Name"
                                    helperText='For example: "/path/to/certificate.pfx"'
                                    isFormElement
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <StriimButton
                                    onClick={() => {
                                        setFileUploadVisibility(val => !val);
                                    }}
                                >
                                    Select File
                                </StriimButton>
                            </Grid>
                        </Grid>
                        {fileUploadVisibility && (
                            <FileSelectionModal
                                setIsModalShown={setFileUploadVisibility}
                                onFileDone={file => {
                                    setFieldValue("properties.CertificateName", file);
                                }}
                            />
                        )}
                        <div
                            data-test-id="vaults-encrypted-data-field"
                            onClick={() => {
                                if (dataForEdit) {
                                    setIsCertificatePasswordEditable(true);
                                }
                            }}
                        >
                            <StriimInputField
                                name="properties.CertificatePassword"
                                label="Certificate Password"
                                inputRef={certificatePasswordRef}
                                isFormElement
                                sx={sxStyles.container}
                                type="password"
                                placeholder={dataForEdit ? "(encrypted)" : null}
                                disabled={dataForEdit ? !isCertificatePasswordEditable : false}
                                previewPassword
                                helperText={
                                    dataForEdit
                                        ? "Click on the input field, to enter a new certificate password"
                                        : 'For example: "password123"'
                                }
                                onChange={event => {
                                    handleCertificatePasswordField(event);
                                }}
                                onBlur={event => {
                                    handleCertificatePasswordField(event);
                                }}
                            />
                        </div>
                    </>
                );
            }}
        </StriimForm>
    );
};

export default CyberArkVault;
